import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@mui/material';
import Modal from '../../../components/Modal';
import Dropdown from '../../../components/controls/Dropdown';
import AddAttributeValueToNarrativeForm from './AddAttributeValueToNarrative/AddAttributeValueToNarrativeForm';
import AddSymptomToNarrativeForm from './AddSymptomToNarrative/AddSymptomToNarrativeForm';
import AddDeniedSymptomToNarrativeForm from './AddDeniedSymptomToNarrative/AddDeniedSymptomToNarrativeForm';
import AddMedicationToNarrativeForm from './AddMedicationToNarrative/AddMedicationToNarrativeForm';
import IncludeDxhxAdditionForm from './IncludeDxhx/IncludeDxhxAdditionForm';
import AddScoreToReportForm from './AddScoreToReport/AddScoreToReportForm';
import AnchorMedsSectionInReportForm from './AnchorMedsSectionInReport/AnchorMedsSectionInReportForm';
import OfferTemplatesForm from './OfferTemplates/OfferTemplatesForm';
import MakeThisAModuleForm from './MakeThisAModule/MakeThisAModuleForm';

const ACTION_TYPES = {
  ADD_ATTRIBUTE_VALUE_TO_NARRATIVE: 'add_attribute_value_to_narrative',
  ADD_SYMPTOM_TO_NARRATIVE: 'add_symptom_to_narrative',
  ADD_DENIED_SYMPTOM_TO_NARRATIVE: 'add_denied_symptom_to_narrative',
  ADD_MEDICATION_TO_NARRATIVE: 'add_medication_to_narrative',
  INCLUDE_DXHX: 'include_dxhx',
  ADD_SCORE_TO_REPORT: 'add_score_to_report',
  ANCHOR_MEDS_SECTION_IN_REPORT: 'anchor_meds_section_in_report',
  OFFER_TEMPLATES: 'offer_templates',
  MAKE_THIS_A_MODULE: 'make_this_a_module',
};

const ACTION_OPTIONS = [
  { id: ACTION_TYPES.ADD_ATTRIBUTE_VALUE_TO_NARRATIVE, name: 'Add Attribute Value To Narrative' },
  { id: ACTION_TYPES.ADD_SYMPTOM_TO_NARRATIVE, name: 'Add Symptom To Narrative' },
  { id: ACTION_TYPES.ADD_DENIED_SYMPTOM_TO_NARRATIVE, name: 'Add Denied Symptom To Narrative' },
  { id: ACTION_TYPES.ADD_MEDICATION_TO_NARRATIVE, name: 'Add Medication To Narrative' },
  { id: ACTION_TYPES.INCLUDE_DXHX, name: 'Include A DxHx' },
  { id: ACTION_TYPES.ADD_SCORE_TO_REPORT, name: 'Add Score To Report' },
  { id: ACTION_TYPES.ANCHOR_MEDS_SECTION_IN_REPORT, name: 'Anchor Meds Section In Report' },
  { id: ACTION_TYPES.OFFER_TEMPLATES, name: 'Offer Templates With Report' },
  { id: ACTION_TYPES.MAKE_THIS_A_MODULE, name: 'Make this a Module' },
];

function OtherActionSelector(props) {
  const { actionListId, reloadCallback } = props;
  const [selectedAction, setSelectedAction] = useState('');
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const handleActionChange = (event) => {
    setSelectedAction(event.target.value);
    setModalIsOpen(true);
  };

  const handleModalClose = () => {
    setModalIsOpen(false);
    setSelectedAction('');
  };

  const onSuccess = () => {
    reloadCallback();
    setModalIsOpen(false);
    setSelectedAction(''); // Reset selection after successful action
  };

  const getModalTitle = () => {
    switch (selectedAction) {
      case ACTION_TYPES.OFFER_TEMPLATES:
        return 'Select a list of templates to offer';
      case ACTION_TYPES.ADD_SYMPTOM_TO_NARRATIVE:
        return 'Add Symptom To Narrative';
      case ACTION_TYPES.ADD_DENIED_SYMPTOM_TO_NARRATIVE:
        return 'Add Denied Symptom To Narrative';
      case ACTION_TYPES.ADD_MEDICATION_TO_NARRATIVE:
        return 'Add Medication To Narrative';
      case ACTION_TYPES.INCLUDE_DXHX:
        return 'Include Dxhx';
      case ACTION_TYPES.ADD_SCORE_TO_REPORT:
        return 'Add Score To Report';
      case ACTION_TYPES.ANCHOR_MEDS_SECTION_IN_REPORT:
        return 'Anchor Meds Section In Report';
      case ACTION_TYPES.ADD_ATTRIBUTE_VALUE_TO_NARRATIVE:
        return 'Add Attribute Value To Narrative';
      case ACTION_TYPES.MAKE_THIS_A_MODULE:
        return 'Reassigning the currently selected action list to a new module';
      default:
        return '';
    }
  };

  const renderModalContent = () => {
    switch (selectedAction) {
      case ACTION_TYPES.OFFER_TEMPLATES:
        return (
          <OfferTemplatesForm
            actionListId={actionListId}
            onSuccess={onSuccess}
          />
        );
      case ACTION_TYPES.ADD_DENIED_SYMPTOM_TO_NARRATIVE:
        return (
          <AddDeniedSymptomToNarrativeForm
            actionListId={actionListId}
            onSuccess={onSuccess}
          />
        );
      case ACTION_TYPES.ADD_MEDICATION_TO_NARRATIVE:
        return (
          <AddMedicationToNarrativeForm
            actionListId={actionListId}
            onSuccess={onSuccess}
          />
        );
      case ACTION_TYPES.INCLUDE_DXHX:
        return (
          <IncludeDxhxAdditionForm
            actionListId={actionListId}
            onSuccess={onSuccess}
          />
        );
      case ACTION_TYPES.ADD_SYMPTOM_TO_NARRATIVE:
        return (
          <AddSymptomToNarrativeForm
            actionListId={actionListId}
            onSuccess={onSuccess}
          />
        );
      case ACTION_TYPES.ADD_SCORE_TO_REPORT:
        return (
          <AddScoreToReportForm
            actionListId={actionListId}
            onSuccess={onSuccess}
          />
        );
      case ACTION_TYPES.ADD_ATTRIBUTE_VALUE_TO_NARRATIVE:
        return (
          <AddAttributeValueToNarrativeForm
            actionListId={actionListId}
            onSuccess={onSuccess}
          />
        );
      case ACTION_TYPES.MAKE_THIS_A_MODULE:
        return (
          <MakeThisAModuleForm
            actionListId={actionListId}
            onSuccess={onSuccess}
          />
        );
      case ACTION_TYPES.ANCHOR_MEDS_SECTION_IN_REPORT:
        return (
          <AnchorMedsSectionInReportForm
            actionListId={actionListId}
            onSuccess={onSuccess}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Grid container sx={{ margin: '6px', maxWidth: '300px' }}>
      <Grid item xs={12}>
        <Dropdown
          name="actionType"
          value={selectedAction}
          onChange={handleActionChange}
          label="Other Actions"
          options={ACTION_OPTIONS}
          size="small"
          fullWidth
        />
      </Grid>

      <Modal
        isOpen={modalIsOpen}
        handleClose={handleModalClose}
        title={getModalTitle()}
      >
        {renderModalContent()}
      </Modal>
    </Grid>
  );
}

OtherActionSelector.propTypes = {
  actionListId: PropTypes.number.isRequired,
  reloadCallback: PropTypes.func.isRequired,
};

export default OtherActionSelector;
